// One Zustand vanilla store per view (E09-S2-T2), in two slices: `session` mirrors the protocol and
// is written only by applyServerEvent; `view` belongs to this view alone. The state is plain data —
// the methods live on the store object — so two stores fed one event log compare equal.
import { createStore } from "zustand/vanilla";
import { POINT_OFF } from "./point.js";
import { appendUserMessage, applyServerEvent, initialSession } from "./reduce.js";
/** The voice default until E13-S5 supplies the real one: every control does nothing. */
export const noopVoice = Object.freeze({
    start: () => undefined,
    end: () => undefined,
    cancel: () => undefined,
    setSpeak: () => undefined,
    setHandsFree: () => undefined,
});
export function initialView(posture, capabilities) {
    return {
        posture,
        capabilities,
        panel: null,
        draft: "",
        speaking: null,
        speak: false,
        frame: null,
        resumeAsked: false,
        pauseAsked: false,
        returnTo: null,
        scroll: null,
        suggested: { sessionId: null, ids: [] },
        loopsSent: { sessionId: null, ids: [] },
        acked: { sessionId: null, acks: [] },
        stepActed: { sessionId: null, rev: null },
        startSent: { sessionId: null, rev: null },
        notesSent: { sessionId: null, ids: [] },
        clip: { sessionId: null, cardId: null, titleId: null, state: "ready", position: 0 },
        mic: null,
        handsFree: null,
        disclosure: { sessionIds: [], open: false },
        point: POINT_OFF,
    };
}
/**
 * How many session ids a view remembers having disclosed for. A view sees few sessions in its life;
 * the bound only stops the list growing without end. Past it the oldest id is forgotten, and should
 * that session come back the notice shows again: the bound errs towards disclosing, never against.
 */
export const MAX_DISCLOSED = 16;
/**
 * The AI disclosure's rule (E09-S6-T5): the first state this view sees for a session id opens the
 * notice, and a later state for the same id leaves it as it is. It reads the session id the state
 * itself carries, so a state that reaches a view without a welcome (a view attached to a running
 * session, a snapshot after a resume) counts the same as one after a welcome. Nothing else is read:
 * not the scaffolding level, since quiet reduces guidance and not disclosure, and not anything the
 * assistant said.
 */
export function disclose(view, state) {
    const id = state?.session.id;
    if (id === undefined || view.disclosure.sessionIds.includes(id))
        return view;
    return {
        ...view,
        disclosure: {
            sessionIds: [...view.disclosure.sessionIds, id].slice(-MAX_DISCLOSED),
            open: true,
        },
    };
}
export function createSurfaceStore(options) {
    const store = createStore()(() => ({
        session: initialSession(),
        view: initialView(options.posture, options.capabilities),
    }));
    const session = (fn) => {
        store.setState((st) => {
            const next = fn(st.session);
            return next === st.session ? st : { session: next };
        });
    };
    return Object.assign(store, {
        receive(event) {
            if (event.t === "speech")
                options.onSpeech?.(event);
            // The disclosure is opened in the same update that brings the state, so no render ever shows
            // a session's first state without its notice.
            store.setState((st) => {
                const next = applyServerEvent(st.session, event);
                if (next === st.session)
                    return st;
                const view = disclose(st.view, next.state);
                return view === st.view ? { session: next } : { session: next, view };
            });
        },
        setConnection(status) {
            session((s) => (s.connection === status ? s : { ...s, connection: status }));
        },
        echoUtterance(utteranceId, text, at) {
            session((s) => appendUserMessage(s, utteranceId, text, { local: true, ...(at ? { at } : {}) }));
        },
        setView(patch) {
            store.setState((st) => ({ view: { ...st.view, ...patch } }));
        },
        setFrame(frame) {
            store.setState((st) => ({ view: { ...st.view, frame } }));
        },
    });
}
//# sourceMappingURL=store.js.map